import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

/// Where a visitor came from, as the site first saw it: the campaign tags on the link (utm_*; ChatGPT adds
/// utm_source=chatgpt.com to every link it gives), a click id (fbclid, gclid), or the outside page that linked here.
export interface VisitSource {
    Source: string | null;
    Medium: string | null;
    Campaign: string | null;
    /// The outside site that sent the visitor (its host), when the browser said.
    Referrer: string | null;
    /// The page they landed on, without its query.
    Landing: string | null;
    /// When that visit was.
    At: string;
}

const KEY = 'ph_visit_source';
const KEEP_DAYS = 90;

/// Remembers how a visitor reached the site, so a shop application can say (the owner, 2026-11-01: five applications came
/// in and only the server's access log could tell they came from ChatGPT). Kept in this browser only, as the last visit that
/// had a source: a visit typed in or from a bookmark never replaces one that came from somewhere. Sent with a shop
/// application (StoreService) and kept on it; nothing else reads it.
@Injectable({ providedIn: 'root' })
export class VisitSourceService {
    constructor(@Inject(PLATFORM_ID) private platformId: any) { }

    /// Once, when the site loads in the browser: reads the address and the referrer of this first page.
    capture(): void {
        if (!isPlatformBrowser(this.platformId)) { return; }
        try {
            const params = new URLSearchParams(window.location.search || '');
            const pick = (k: string) => (params.get(k) || '').trim().slice(0, 80) || null;
            let source = pick('utm_source');
            let medium = pick('utm_medium');
            const campaign = pick('utm_campaign');
            if (!source && params.get('fbclid')) { source = 'facebook'; medium = medium || 'click'; }
            if (!source && (params.get('gclid') || params.get('gbraid') || params.get('wbraid'))) { source = 'google'; medium = medium || 'cpc'; }
            const referrer = VisitSourceService.outsideHost(document.referrer);
            const now = new Date().toISOString();
            const landing = (window.location.pathname || '/').slice(0, 120);
            const kept = this.read();
            if (!source && !referrer) {
                // A direct visit: noted only when nothing better is known.
                if (!kept) { this.write({ Source: null, Medium: null, Campaign: null, Referrer: null, Landing: landing, At: now }); }
                return;
            }
            this.write({ Source: source, Medium: medium, Campaign: campaign, Referrer: referrer, Landing: landing, At: now });
        } catch { /* storage refused (a private window): the application goes without a source */ }
    }

    /// What goes with a shop application; null when nothing is known.
    current(): VisitSource | null {
        return isPlatformBrowser(this.platformId) ? this.read() : null;
    }

    private read(): VisitSource | null {
        try {
            const raw = localStorage.getItem(KEY);
            if (!raw) { return null; }
            const v = JSON.parse(raw) as VisitSource;
            const at = Date.parse(v && v.At);
            if (isNaN(at) || Date.now() - at > KEEP_DAYS * 24 * 3600 * 1000) { localStorage.removeItem(KEY); return null; }
            return v;
        } catch { return null; }
    }

    private write(v: VisitSource): void {
        try { localStorage.setItem(KEY, JSON.stringify(v)); } catch { }
    }

    /// The referring page's host when it is another site (not this one, not a payment or sign-in hop back).
    private static outsideHost(referrer: string): string | null {
        if (!referrer) { return null; }
        try {
            const host = new URL(referrer).hostname.toLowerCase();
            if (!host || host === window.location.hostname.toLowerCase() || host.endsWith('.' + window.location.hostname.toLowerCase())) { return null; }
            return host.slice(0, 80);
        } catch { return null; }
    }
}
